import { deleteCookie, getCookie, setCookie } from "cookies-next";

/**
 * Token store for the admin panel.
 *
 * Tokens live in cookies rather than memory because `middleware.ts` has to read
 * them to gate routes before React runs, and the non-React `apiFetch` client has
 * to read them synchronously. That rules out httpOnly, so these cookies ARE
 * readable by JavaScript — a deliberate trade, not an oversight.
 *
 * What compensates for it, and what must not be quietly "simplified" away:
 *   - a 15-minute access token, so a stolen one expires quickly;
 *   - refresh rotation with server-side blacklisting, so a stolen refresh token
 *     stops working the moment the real session refreshes;
 *   - maxAge tied to the refresh lifetime, so the cookie never outlives the
 *     token inside it and leaves a session that looks valid but is not;
 *   - sameSite and secure set explicitly below.
 *
 * The names are panel-specific so this and the customer-facing Athinex app can
 * both be open on one host without overwriting each other's session.
 */

export const ACCESS_TOKEN_COOKIE = "lg_admin_token";
export const REFRESH_TOKEN_COOKIE = "lg_admin_refresh";
export const USER_COOKIE = "lg_admin_user";

// Matches SIMPLE_JWT.REFRESH_TOKEN_LIFETIME (ADMIN_JWT_REFRESH_DAYS, default 1).
const MAX_AGE = 24 * 60 * 60;

function cookieOptions() {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:";
  return { maxAge: MAX_AGE, sameSite: "lax" as const, secure, path: "/" };
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  full_name: string;
  is_staff: boolean;
  is_superuser: boolean;
}

export function getAccessToken(): string | undefined {
  const v = getCookie(ACCESS_TOKEN_COOKIE);
  return typeof v === "string" ? v : undefined;
}

export function getRefreshToken(): string | undefined {
  const v = getCookie(REFRESH_TOKEN_COOKIE);
  return typeof v === "string" ? v : undefined;
}

/**
 * Whether this browser holds an access token.
 *
 * Server-safe: during SSR there is no document, and the honest answer there is
 * "no" — the client re-evaluates after hydration. Callers use it to avoid
 * firing a request that could only ever answer 401.
 */
export function hasAccessToken(): boolean {
  if (typeof document === "undefined") return false;
  return !!getAccessToken();
}

export function setTokens(access: string, refresh?: string): void {
  setCookie(ACCESS_TOKEN_COOKIE, access, cookieOptions());
  if (refresh) setCookie(REFRESH_TOKEN_COOKIE, refresh, cookieOptions());
}

export function setStoredUser(user: AdminUser): void {
  setCookie(USER_COOKIE, JSON.stringify(user), cookieOptions());
}

export function getStoredUser(): AdminUser | null {
  const raw = getCookie(USER_COOKIE);
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  deleteCookie(ACCESS_TOKEN_COOKIE, { path: "/" });
  deleteCookie(REFRESH_TOKEN_COOKIE, { path: "/" });
  deleteCookie(USER_COOKIE, { path: "/" });
}
